import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, Award, Mail, ExternalLink } from "lucide-react";
import SEO from "../components/SEO";
import "./BlogArticle.css";

export const ReduceCorporationTaxArticle = () => {
  // Schema markup for SEO
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "How to Reduce Corporation Tax UK",
    "description": "Legal ways to reduce Corporation Tax for UK limited companies",
    "author": {
      "@type": "Organization",
      "name": "Brightson Accounting"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Brightson Accounting",
      "logo": {
        "@type": "ImageObject",
        "url": "https://brightsonaccounting.co.uk/logo.png"
      }
    },
    "datePublished": "2026-04-02",
    "dateModified": "2026-04-02",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://brightsonaccounting.co.uk/blog/reduce-corporation-tax-uk"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How can I reduce Corporation Tax legally in the UK?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "By claiming all allowable business expenses, using a mix of salary and dividends for tax-efficient income extraction, contributing to pensions, utilizing capital allowances, and implementing strategic tax planning with proper timing of income and expenses."
        }
      },
      {
        "@type": "Question",
        "name": "What expenses reduce Corporation Tax?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Business-related costs such as rent, home office costs, software and subscriptions, professional fees, travel and mileage, and equipment. All expenses must be wholly and exclusively for business purposes to be HMRC-compliant."
        }
      },
      {
        "@type": "Question",
        "name": "Can pension contributions reduce Corporation Tax?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, employer pension contributions are tax deductible and reduce your Corporation Tax bill while building long-term wealth. They are one of the most tax-efficient strategies for limited company owners."
        }
      }
    ]
  };

  return (
    <div className="article-page">
      <SEO
        title="Reduce Corporation Tax UK | 2026 Legal Strategies"
        description="Learn how to legally reduce your Corporation Tax bill in the UK. Proven strategies for limited companies from Brightson Accounting."
        canonical="/blog/reduce-corporation-tax-uk"
        keywords="reduce corporation tax UK, corporation tax planning UK, how to pay less corporation tax, UK corporation tax tips, tax saving strategies limited company, accountant Wolverhampton, small business accountant West Midlands"
        schema={JSON.stringify([articleSchema, faqSchema])}
      />

      {/* Article Header */}
      <section className="article-header">
        <div className="container">
          <div className="article-back">
            <Link to="/blog" className="back-link">
              <ArrowLeft size={20} />
              Back to Blog
            </Link>
          </div>
          <div className="article-header-content">
            <span className="article-category-badge">Tax Advisory</span>
            <h1 className="article-main-title">How to Reduce Your Corporation Tax Bill Legally (UK Guide 2026)</h1>
            <p className="article-subtitle">Smart Strategies for Limited Companies to Pay Less Tax</p>
            <div className="article-meta-bar">
              <div className="meta-author">
                <Award size={18} />
                <span>Brightson Accounting (FCCA Qualified)</span>
              </div>
              <div className="meta-info">
                <Calendar size={16} />
                <span>2 April 2026</span>
                <Clock size={16} />
                <span>7 min read</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="article-content">
        <div className="container">
          <div className="article-body">
            
            {/* Traffic boost hook */}
            <div style={{ background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: '6px', padding: '1rem', marginBottom: '2rem' }}>
              <p style={{ margin: 0, fontSize: '0.95rem', color: '#92400e' }}>
                Most business owners overpay tax — <Link to="/corporation-tax-calculator" style={{ color: '#92400e', fontWeight: 600, textDecoration: 'underline' }}>see how much you could save using our calculator 👉</Link>
              </p>
            </div>

            <p className="article-lead">
              Paying Corporation Tax is unavoidable — overpaying isn't.
            </p>

            <p>
              If you run a limited company in the UK, there are fully legal, HMRC-compliant ways to reduce your Corporation Tax bill.
            </p>

            <p>
              This guide explains the most effective <strong>tax saving strategies</strong> used by smart business owners across Wolverhampton and the West Midlands.
            </p>

            <div className="info-box" style={{marginTop: '2rem'}}>
              <h2 style={{fontSize: '1.5rem', marginTop: '0', paddingTop: '0', borderTop: 'none'}}>How to Reduce Corporation Tax (Quick Summary)</h2>
              <ul style={{marginBottom: '0'}}>
                <li><strong>Claim all allowable expenses</strong></li>
                <li><strong>Pay yourself via salary + dividends</strong></li>
                <li><strong>Use capital allowances</strong></li>
                <li><strong>Make pension contributions</strong></li>
                <li><strong>Use loss relief</strong></li>
                <li><strong>Plan timing of income</strong></li>
              </ul>
            </div>

            <h2>Claim All Allowable Business Expenses</h2>

            <p>
              The simplest way to <strong>reduce Corporation Tax</strong> is to ensure you're claiming all business expenses that are wholly and exclusively for your company.
            </p>

            <p>
              Common allowable expenses include:
            </p>

            <ul>
              <li><strong>Rent or home office costs</strong> (proportionate to business use)</li>
              <li><strong>Software and subscriptions</strong> (accounting software, CRM tools, etc.)</li>
              <li><strong>Professional fees</strong> (accountant, solicitor, consultants)</li>
              <li><strong>Travel and mileage</strong> (business trips only)</li>
              <li><strong>Equipment</strong> (laptops, phones, office furniture)</li>
            </ul>

            <p className="highlight-note">
              Missing even small expenses throughout the year increases your tax bill unnecessarily.
            </p>

            <h2>Pay Yourself Tax Efficiently</h2>

            <p>
              Most <Link to="/services" className="inline-link">accountants in Wolverhampton</Link> recommend using a combination of:
            </p>

            <ul>
              <li><strong>Salary</strong> (paid within personal allowance and National Insurance thresholds)</li>
              <li><strong>Dividends</strong> (for remaining income extraction)</li>
            </ul>

            <p>
              This approach reduces overall tax liability compared to salary-only extraction, as dividends are taxed at 
              lower rates and don't attract National Insurance contributions.
            </p>

            <p>
              For 2026/27, consider:
            </p>

            <ul>
              <li>Setting salary at the personal allowance threshold (£12,570)</li>
              <li>Extracting remaining profits as dividends</li>
              <li>Planning dividend payments across tax years</li>
            </ul>

            <h3>2026 Dividend Tax Rates</h3>

            <p>
              Dividend allowance: <strong>£500</strong>
            </p>

            <p>
              Dividend tax rates (from April 2026):
            </p>

            <ul>
              <li><strong>Basic rate:</strong> 10.75%</li>
              <li><strong>Higher rate:</strong> 35.75%</li>
              <li><strong>Additional rate:</strong> 39.35%</li>
            </ul>

            <p>
              While dividends reduce National Insurance, they are paid from post-tax profits. The optimal strategy is 
              finding the right balance between salary and dividends.
            </p>

            <h3>Extraction Method Comparison</h3>

            <div className="table-responsive">
              <table className="deadline-table">
                <thead>
                  <tr>
                    <th>Extraction Method</th>
                    <th>Corp Tax Deductible</th>
                    <th>Employee NI</th>
                    <th>Employer NI</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Salary</td>
                    <td>Yes</td>
                    <td>Yes</td>
                    <td>Yes</td>
                  </tr>
                  <tr>
                    <td>Dividends</td>
                    <td>No</td>
                    <td>No</td>
                    <td>No</td>
                  </tr>
                  <tr>
                    <td>Pension</td>
                    <td>Yes</td>
                    <td>No</td>
                    <td>No</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2>Use Capital Allowances</h2>

            <p>
              Capital allowances allow you to claim tax relief on business assets and equipment:
            </p>

            <ul>
              <li><strong>Laptops and computers</strong></li>
              <li><strong>Office furniture</strong></li>
              <li><strong>Machinery and tools</strong></li>
              <li><strong>Vehicles</strong> (subject to specific rules)</li>
            </ul>

            <p>
              The <strong>Annual Investment Allowance (AIA)</strong> allows you to deduct the full value of qualifying 
              assets (up to £1 million) from your profits before tax.
            </p>

            <p>
              Limited companies can also use <strong>Full Expensing</strong>, allowing a 100% deduction on qualifying 
              plant and machinery with no upper limit.
            </p>

            <p>
              With writing-down allowances reducing to around <strong>14%</strong> from April 2026, upfront relief through 
              AIA or Full Expensing is more valuable than ever.
            </p>

            <p>
              A new <strong>40% First-Year Allowance (FYA)</strong> may apply in certain cases, particularly for leased assets.
            </p>

            <p className="note-text">
              Strategic timing of asset purchases before year-end can significantly reduce your Corporation Tax bill.
            </p>

            {/* Mid-Article CTA */}
            <div className="article-cta-box" style={{ background: '#f8f9fa', border: '2px solid #0066cc', marginTop: '2rem', marginBottom: '2rem' }}>
              <h3>💡 Want to see how much tax you could save?</h3>
              <p>
                Most UK business owners are overpaying without realising it.
              </p>
              <Link to="/corporation-tax-calculator" className="article-cta-button">
                👉 Try the Corporation Tax Calculator
              </Link>
            </div>

            <h2>Contribute to a Pension</h2>

            <p>
              Employer pension contributions are one of the most <strong>tax-efficient strategies</strong> for limited company owners:
            </p>

            <ul>
              <li><strong>Reduces Corporation Tax</strong> as contributions are fully deductible</li>
              <li><strong>Builds long-term wealth</strong> with tax-free growth</li>
              <li><strong>No National Insurance</strong> on employer contributions</li>
              <li><strong>Annual allowance</strong> of £60,000 (2026/27)</li>
            </ul>

            <p>
              Consult with a <Link to="/contact" className="inline-link">qualified accountant</Link> to structure contributions optimally.
            </p>

            <p className="info-box">
              💡 <strong>Pro Tip:</strong> Pension contributions must be physically paid before your company year-end to 
              qualify for Corporation Tax relief. Accruing them in accounts is not sufficient.
            </p>

            <h2>Use Loss Relief</h2>

            <p>
              If your company makes a loss, you can use it to reduce tax in several ways:
            </p>

            <ul>
              <li><strong>Carry back losses</strong> to offset against previous year's profits (claim refund)</li>
              <li><strong>Carry forward losses</strong> to reduce future Corporation Tax bills</li>
              <li><strong>Group relief</strong> if you have multiple companies</li>
            </ul>

            <p>
              This is particularly valuable for start-ups or businesses investing heavily in growth.
            </p>

            <h2>Claim R&D Tax Relief</h2>

            <p>
              Under the <strong>Merged R&D Scheme</strong>, businesses can claim a taxable credit (around 20%) on 
              qualifying innovation expenditure.
            </p>

            <p>
              If your company is 'R&D Intensive' (spending 30%+ of total costs on R&D), you may qualify for enhanced 
              support under <strong>ERIS (Enhanced R&D Intensive Support)</strong>.
            </p>

            <p>
              Qualifying R&D activities include developing new products, processes, or services that advance knowledge 
              or capability in your field.
            </p>

            <h2>Plan Timing of Income & Expenses</h2>

            <p>
              Strategic <strong>corporation tax planning</strong> includes timing:
            </p>

            <ul>
              <li><strong>Delay invoicing</strong> near year-end to defer income (if cash flow permits)</li>
              <li><strong>Accelerate expenses</strong> by purchasing before year-end</li>
              <li><strong>Make large investments</strong> before accounting period closes</li>
              <li><strong>Time contract commencement</strong> to spread income across tax years</li>
            </ul>

            <h2>Understand Corporation Tax Rates (2026/27)</h2>

            <p>
              UK Corporation Tax operates on a tiered system:
            </p>

            <ul>
              <li><strong>19%</strong> – Small Profits Rate (profits up to £50,000)</li>
              <li><strong>25%</strong> – Main Rate (profits over £250,000)</li>
              <li><strong>Marginal relief</strong> – Applies to profits between £50,000 and £250,000</li>
            </ul>

            <p>
              Proper planning can keep you within lower tax bands or reduce your effective rate through marginal relief.
            </p>

            <p className="highlight-note">
              ⚠️ <strong>Warning:</strong> If you run multiple 'associated companies', the £50,000 and £250,000 profit 
              thresholds are split between them. This can push you into higher tax rates sooner.
            </p>

            <p className="info-box">
              💡 If you own multiple companies, tax planning should be done across your entire group, not individually.
            </p>

            <h3>Marginal Relief Quick Reference</h3>

            <div className="table-responsive">
              <table className="deadline-table">
                <thead>
                  <tr>
                    <th>Company Profit</th>
                    <th>Tax Rate</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Under £50,000</td>
                    <td>19%</td>
                    <td>Small Profits Rate</td>
                  </tr>
                  <tr>
                    <td>£50,000–£250,000</td>
                    <td>~25% (with relief)</td>
                    <td>Marginal Relief Zone</td>
                  </tr>
                  <tr>
                    <td>Over £250,000</td>
                    <td>25%</td>
                    <td>Main Rate</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2>Common Corporation Tax Mistakes to Avoid</h2>

            <p>
              Many business owners unknowingly increase their tax bill by:
            </p>

            <ul>
              <li><strong>Missing allowable expenses</strong> (lack of proper record-keeping)</li>
              <li><strong>Poor tax planning</strong> (reactive rather than proactive approach)</li>
              <li><strong>Mixing personal and business spending</strong> (non-allowable expenses)</li>
              <li><strong>Missing deadlines</strong> (resulting in penalties and interest)</li>
              <li><strong>Not seeking professional advice</strong> for <Link to="/services" className="inline-link">tax advice for limited companies</Link></li>
            </ul>

            <div className="article-cta-box">
              <h2>Want to Reduce Your Tax Bill?</h2>
              <p>
                Our FCCA-qualified accountants help limited companies across Wolverhampton and the West Midlands 
                implement proven tax-saving strategies.
              </p>
              <p>
                We work with businesses across Wolverhampton and the West Midlands and understand local challenges, 
                including multi-company structures and evolving tax rules.
              </p>
              <Link to="/contact" className="article-cta-button">
                <Mail size={20} />
                Book a Free Consultation
              </Link>
            </div>

            <h2>Frequently Asked Questions</h2>

            <div className="faq-section">
              <div className="faq-item">
                <h3>How can I reduce Corporation Tax legally in the UK?</h3>
                <p>
                  By claiming all allowable business expenses, using a mix of salary and dividends for tax-efficient 
                  income extraction, contributing to pensions, utilizing capital allowances, and implementing strategic 
                  tax planning with proper timing of income and expenses.
                </p>
              </div>

              <div className="faq-item">
                <h3>What expenses reduce Corporation Tax?</h3>
                <p>
                  Business-related costs such as rent, home office costs, software and subscriptions, professional fees, 
                  travel and mileage, and equipment. All expenses must be wholly and exclusively for business purposes 
                  to be HMRC-compliant.
                </p>
              </div>

              <div className="faq-item">
                <h3>Can pension contributions reduce Corporation Tax?</h3>
                <p>
                  Yes, employer pension contributions are tax deductible and reduce your Corporation Tax bill while 
                  building long-term wealth. They are one of the most tax-efficient strategies for limited company owners.
                </p>
              </div>
            </div>

            <div className="disclaimer">
              <h3>Disclaimer</h3>
              <p>
                This article is for general guidance only and based on UK tax rules as of April 2026. Tax legislation 
                may change. For tailored advice specific to your business, contact Brightson Accounting.
              </p>
            </div>

          </div>

          {/* Sidebar */}
          <aside className="article-sidebar">
            <div className="sidebar-card">
              <h3 className="sidebar-heading">Need Expert Tax Advice?</h3>
              <p>Our FCCA-qualified accountants specialise in corporation tax planning for West Midlands businesses.</p>
              <Link to="/contact" className="sidebar-button">
                <Mail size={18} />
                Contact Us
              </Link>
            </div>

            <div className="sidebar-card">
              <h3 className="sidebar-heading">Related Articles</h3>
              <div className="related-links">
                <Link to="/blog/tax-deadlines-small-businesses-2026-27" className="related-link">
                  Tax Deadlines 2026/27
                </Link>
                <Link to="/blog" className="related-link">Making Tax Digital Guide</Link>
                <Link to="/blog" className="related-link">Self Assessment for Directors</Link>
              </div>
            </div>

            <div className="sidebar-card">
              <h3 className="sidebar-heading">Our Services</h3>
              <div className="related-links">
                <Link to="/services#tax-planning" className="related-link">Tax Planning & Advisory</Link>
                <Link to="/services#annual-accounts" className="related-link">Annual Accounts</Link>
                <Link to="/pricing" className="related-link">Pricing</Link>
                <Link to="/contact" className="related-link">Contact</Link>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default ReduceCorporationTaxArticle;
